import { useEffect, useState } from "react";

import { Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  ContestScreeningResult,
  ContestSubmissionsFilters,
} from "@/types/contest-submission";

interface Props {
  filters: ContestSubmissionsFilters;
  onFiltersChange: (next: ContestSubmissionsFilters) => void;
}

/**
 * 검색 input — debounce 400ms 로 타이핑 끝난 뒤 부모로 전파.
 * 부모는 검색 변경 시 page=1 로 리셋해 list 가 처음부터 다시 보이게 한다.
 */
const SEARCH_DEBOUNCE_MS = 400;

const SCREENING_ALL = "ALL";

const SCREENING_OPTIONS: Array<{ value: string; label: string }> = [
  { value: SCREENING_ALL, label: "심사 전체" },
  { value: "PENDING", label: "검토중" },
  { value: "PASS", label: "적격" },
  { value: "FAIL", label: "부적격" },
];

export function ContestSubmissionFilters({ filters, onFiltersChange }: Props) {
  const [draft, setDraft] = useState(filters.search ?? "");

  // 외부에서 reset 한 경우 (ex. 초기화 버튼) 로컬 상태도 동기화 —
  // effect 대신 렌더 중 상태 조정 패턴 (react-hooks/set-state-in-effect 준수).
  const externalSearch = filters.search ?? "";
  const [prevExternalSearch, setPrevExternalSearch] = useState(externalSearch);
  if (prevExternalSearch !== externalSearch) {
    setPrevExternalSearch(externalSearch);
    setDraft(externalSearch);
  }

  useEffect(
    function emitDebounced() {
      const t = window.setTimeout(() => {
        const next = draft.trim();
        if ((filters.search ?? "") === next) return;
        onFiltersChange({ ...filters, search: next || undefined });
      }, SEARCH_DEBOUNCE_MS);
      return () => window.clearTimeout(t);
    },
    [draft, filters, onFiltersChange],
  );

  const hasActiveFilters = Boolean(filters.search || filters.screeningResult);

  const clearFilters = () => {
    setDraft("");
    onFiltersChange({});
  };

  const handleScreeningChange = (value: string) => {
    onFiltersChange({
      ...filters,
      screeningResult:
        value === SCREENING_ALL
          ? undefined
          : (value as ContestScreeningResult),
    });
  };

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center">
      <div className="relative flex-1 md:max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="이름·필명·이메일·작품 제목 검색"
          className="pl-9"
          aria-label="응모 이력 검색"
        />
      </div>
      <Select
        value={filters.screeningResult ?? SCREENING_ALL}
        onValueChange={handleScreeningChange}
      >
        <SelectTrigger className="w-full md:w-[140px]" aria-label="심사 결과 필터">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SCREENING_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasActiveFilters && (
        <Button
          variant="ghost"
          onClick={clearFilters}
          className="w-full md:w-auto min-h-[44px] md:min-h-0"
          aria-label="필터 초기화"
        >
          <X className="mr-2 h-4 w-4" />
          초기화
        </Button>
      )}
    </div>
  );
}
